import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, Flame, Trophy, MessageCircle, BookOpen, 
  Brain, Target, Shield, Calendar, TrendingUp, Zap,
  Star, Award, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import BottomNav from '../components/BottomNav';

const API = process.env.REACT_APP_BACKEND_URL;

const ProgressPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState(null);
  const [goals, setGoals] = useState(null);
  const [freezeStatus, setFreezeStatus] = useState(null);
  const [milestones, setMilestones] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, historyRes, goalsRes, freezeRes, milestonesRes] = await Promise.all([
        axios.get(`${API}/api/gamification/stats`, { headers }),
        axios.get(`${API}/api/gamification/history`, { headers }),
        axios.get(`${API}/api/gamification/goals`, { headers }),
        axios.get(`${API}/api/gamification/streak-freeze`, { headers }),
        axios.get(`${API}/api/gamification/milestones`, { headers })
      ]);

      setStats(statsRes.data);
      setHistory(historyRes.data);
      setGoals(goalsRes.data);
      setFreezeStatus(freezeRes.data);
      setMilestones(milestonesRes.data);
    } catch (error) {
      console.error('Error fetching progress data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSetGoal = async (goalId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/gamification/goals/set`, 
        { goal_id: goalId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Meta atualizada!');
      fetchAllData();
    } catch (error) {
      toast.error('Erro ao definir meta');
    }
  };

  const handleBuyFreeze = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/gamification/streak-freeze/buy`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        toast.success(res.data.message);
        fetchAllData();
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error('Erro ao comprar proteção');
    }
  };

  const handleUseFreeze = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/gamification/streak-freeze/use`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        toast.success(res.data.message);
        fetchAllData();
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error('Erro ao usar proteção');
    }
  };

  // Activity heatmap colors
  const getActivityColor = (level, isDark) => {
    const colors = isDark
      ? ['#1e293b', '#134e4a', '#0d9488', '#14b8a6', '#2dd4bf']
      : ['#f1f5f9', '#ccfbf1', '#99f6e4', '#5eead4', '#2dd4bf'];
    return colors[level] || colors[0];
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-sand'} flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-sand text-forest'} pb-24`}>
      {/* Header */}
      <div className={`sticky top-0 z-40 ${isDarkMode ? 'bg-gray-900/95' : 'bg-sand/95'} backdrop-blur-sm border-b ${isDarkMode ? 'border-gray-800' : 'border-sage/20'} px-4 py-4`}>
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-semibold">Meu Progresso</h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 px-4 py-3 overflow-x-auto">
        {[
          { id: 'overview', name: 'Visão Geral', icon: TrendingUp },
          { id: 'goals', name: 'Metas', icon: Target },
          { id: 'streak', name: 'Streak', icon: Flame },
          { id: 'milestones', name: 'Conquistas', icon: Trophy }
        ].map(tab => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 whitespace-nowrap ${
              activeTab === tab.id 
                ? 'bg-teal-500 text-white' 
                : isDarkMode ? 'border-gray-700' : ''
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.name}
          </Button>
        ))}
      </div>

      <div className="px-4 py-4 space-y-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  <span className="text-sm opacity-70">XP Total</span>
                </div>
                <p className="text-2xl font-bold">{stats?.xp || 0}</p>
                <p className="text-xs opacity-50">Nível {stats?.level?.level || 1} - {stats?.level?.title}</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className={`p-4 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Flame className="w-5 h-5 text-orange-500" />
                  <span className="text-sm opacity-70">Streak</span>
                </div>
                <p className="text-2xl font-bold">{stats?.streak?.current || 0} dias</p>
                <p className="text-xs opacity-50">Recorde: {stats?.streak?.longest || 0} dias</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className={`p-4 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <MessageCircle className="w-5 h-5 text-blue-500" />
                  <span className="text-sm opacity-70">Mensagens</span>
                </div>
                <p className="text-2xl font-bold">{stats?.totals?.messages || 0}</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className={`p-4 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen className="w-5 h-5 text-green-500" />
                  <span className="text-sm opacity-70">Palavras</span>
                </div>
                <p className="text-2xl font-bold">{stats?.totals?.words_saved || 0}</p>
              </motion.div>
            </div>

            {/* Activity Heatmap */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className={`p-4 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}
            >
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-teal-500" />
                Atividade (últimos 31 dias)
              </h3>
              <div className="grid grid-cols-7 gap-1">
                {/* Day labels */}
                {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((day, i) => (
                  <div key={i} className="text-xs text-center opacity-50 mb-1">{day}</div>
                ))}
                {/* Activity cells */}
                {history?.activity_calendar?.map((day, i) => (
                  <div
                    key={i}
                    className="aspect-square rounded-sm cursor-pointer transition-transform hover:scale-110"
                    style={{ backgroundColor: getActivityColor(day.activity_level, isDarkMode) }}
                    title={`${new Date(day.date).toLocaleDateString('pt-BR')}: ${day.messages} msgs, ${day.reviews} revisões`}
                  />
                ))}
              </div>
              <div className="flex items-center justify-end gap-2 mt-3 text-xs opacity-50">
                <span>Menos</span>
                {[0, 1, 2, 3, 4].map(level => (
                  <div
                    key={level}
                    className="w-3 h-3 rounded-sm"
                    style={{ backgroundColor: getActivityColor(level, isDarkMode) }}
                  />
                ))}
                <span>Mais</span>
              </div>
            </motion.div>

            {/* Summary */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className={`p-4 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}
            >
              <h3 className="font-semibold mb-4">Resumo dos 30 dias</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="opacity-70">Dias ativos</span>
                  <span className="font-semibold">{history?.summary?.days_active || 0} dias</span>
                </div>
                <div className="flex justify-between">
                  <span className="opacity-70">Lições completadas</span>
                  <span className="font-semibold">{history?.summary?.total_lessons || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="opacity-70">Flashcards revisados</span>
                  <span className="font-semibold">{history?.summary?.total_reviews || 0}</span>
                </div>
              </div>
            </motion.div>
          </>
        )}

        {/* Goals Tab */}
        {activeTab === 'goals' && (
          <>
            {/* Today's Progress */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-4 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}
            >
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-teal-500" />
                Progresso de Hoje
              </h3>
              <div className="mb-4">
                <div className="flex justify-between mb-2">
                  <span>{goals?.today_minutes || 0} min</span>
                  <span>{goals?.goal_minutes || 10} min</span>
                </div>
                <div className={`h-3 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} overflow-hidden`}>
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      goals?.goal_completed ? 'bg-green-500' : 'bg-teal-500'
                    }`}
                    style={{ width: `${goals?.goal_progress_percent || 0}%` }}
                  />
                </div>
              </div>
              {goals?.goal_completed && (
                <div className="flex items-center gap-2 text-green-500">
                  <Trophy className="w-5 h-5" />
                  <span className="font-semibold">Meta do dia concluída!</span>
                </div>
              )}
            </motion.div>

            {/* Goal Selection */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className={`p-4 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}
            >
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-teal-500" />
                Escolha sua Meta Diária
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {goals?.available_goals?.map(goal => (
                  <button
                    key={goal.id}
                    onClick={() => handleSetGoal(goal.id)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      goals?.current_goal === goal.id
                        ? 'border-teal-500 bg-teal-500/10'
                        : isDarkMode ? 'border-gray-700 hover:border-gray-600' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`goal-${goal.id}`}
                  >
                    <p className="font-semibold">{goal.name}</p>
                    <p className="text-sm opacity-70">{goal.description}</p>
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}

        {/* Streak Tab */}
        {activeTab === 'streak' && (
          <>
            {/* Current Streak */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-6 rounded-2xl ${isDarkMode ? 'bg-gradient-to-br from-orange-900/50 to-red-900/50' : 'bg-gradient-to-br from-orange-100 to-red-100'} shadow-sm text-center`}
            >
              <Flame className="w-16 h-16 text-orange-500 mx-auto mb-4" />
              <p className="text-4xl font-bold mb-2">{stats?.streak?.current || 0}</p>
              <p className="opacity-70">dias consecutivos</p>
              {freezeStatus?.streak_at_risk && (
                <div className="mt-4 p-3 bg-red-500/20 rounded-xl">
                  <p className="text-red-500 font-semibold">⚠️ Seu streak está em risco!</p>
                  <p className="text-sm opacity-70">Pratique hoje ou use uma proteção</p>
                </div>
              )}
            </motion.div>

            {/* Streak Freeze */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className={`p-4 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}
            >
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-500" />
                Proteção de Streak
              </h3>
              <p className="text-sm opacity-70 mb-4">
                Use proteções para manter seu streak quando não puder praticar
              </p>
              
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  {[0, 1, 2].map(i => (
                    <div
                      key={i}
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        i < (freezeStatus?.freezes_available || 0)
                          ? 'bg-blue-500 text-white'
                          : isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                      }`}
                    >
                      <Shield className="w-5 h-5" />
                    </div>
                  ))}
                </div>
                <span className="text-sm opacity-70">
                  {freezeStatus?.freezes_available || 0}/3 disponíveis
                </span>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleUseFreeze}
                  disabled={!freezeStatus?.freezes_available || freezeStatus?.freezes_available <= 0}
                  className="flex-1 bg-blue-500 hover:bg-blue-600"
                  data-testid="use-freeze-btn"
                >
                  Usar Proteção
                </Button>
                <Button
                  onClick={handleBuyFreeze}
                  variant="outline"
                  disabled={(freezeStatus?.freezes_available || 0) >= 3 || (stats?.xp || 0) < 50}
                  className="flex-1"
                  data-testid="buy-freeze-btn"
                >
                  Comprar (50 XP)
                </Button>
              </div>
            </motion.div>

            {/* Streak Records */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className={`p-4 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}
            >
              <h3 className="font-semibold mb-4">Recordes</h3>
              <div className="flex justify-between">
                <span className="opacity-70">Maior streak</span>
                <span className="font-semibold">{stats?.streak?.longest || 0} dias</span>
              </div>
            </motion.div>
          </>
        )}

        {/* Milestones Tab */}
        {activeTab === 'milestones' && (
          <div className="space-y-4">
            {milestones?.milestones?.map((milestone, i) => (
              <motion.div
                key={milestone.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                className={`p-4 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm ${
                  milestone.achieved ? 'ring-2 ring-teal-500' : ''
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${
                    milestone.achieved 
                      ? 'bg-teal-500/20' 
                      : isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
                  }`}>
                    {milestone.icon}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold">{milestone.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <div className={`flex-1 h-2 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} overflow-hidden`}>
                        <div
                          className={`h-full rounded-full ${milestone.achieved ? 'bg-teal-500' : 'bg-gray-400'}`}
                          style={{ width: `${milestone.progress_percent}%` }}
                        />
                      </div>
                      <span className="text-xs opacity-70">
                        {milestone.current}/{milestone.threshold}
                      </span>
                    </div>
                  </div>
                  {milestone.achieved && (
                    <Award className="w-6 h-6 text-teal-500" />
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default ProgressPage;
